import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, limit, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import './PreparedMailsStrip.css';
import './DuplicateAlertsPanel.css';

/**
 * PreparedMailsStrip — the emails that were written for you while you were away.
 *
 * Both the flow builder's "prepare in backend" mode and outreach jobs running in
 * `deliveryMode: 'prepare'` drop finished emails into
 * `World/{uid}/preparedEmails/{contactId}`. This strip sits above the contact
 * table and turns that collection into something you can act on: open one, open
 * all of them in the Bulk sender, or throw one away.
 *
 * Reads the prepared docs directly rather than the contacts' `has_prepared_email`
 * flag, because only the docs carry the subject line and origin needed on a card.
 */

// Matches the backend cap in functions/processOutreachJobs.js — never more than
// this many can be waiting, so the listener can be bounded safely.
const MAX_STRIP_ITEMS = 60;

const relativeTime = (ms) => {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
  return `${Math.round(diff / 86400000)}d ago`;
};

const OriginStar = () => (
  <svg className="pm-card-origin-star" viewBox="0 0 64 64" aria-hidden="true">
    <path
      fill="currentColor"
      d="M60,28.5H45.062L58,21.03a3.5,3.5,0,0,0,1.281-4.78h0a3.5,3.5,0,0,0-4.78-1.281L41.562,22.438,49.031,9.5a3.5,3.5,0,0,0-1.281-4.78h0A3.5,3.5,0,0,0,42.969,6L35.5,18.938V4A3.5,3.5,0,0,0,32,.5h0A3.5,3.5,0,0,0,28.5,4V18.938L21.031,6a3.5,3.5,0,0,0-4.78-1.281h0A3.5,3.5,0,0,0,14.969,9.5l7.468,12.937L9.5,14.969A3.5,3.5,0,0,0,4.72,16.25h0A3.5,3.5,0,0,0,6,21.03L18.938,28.5H4A3.5,3.5,0,0,0,.5,32v0A3.5,3.5,0,0,0,4,35.5H18.938L6,42.97A3.5,3.5,0,0,0,4.72,47.75h0A3.5,3.5,0,0,0,9.5,49.031l12.937-7.468L14.969,54.5a3.5,3.5,0,0,0,1.281,4.78h0A3.5,3.5,0,0,0,21.031,58L28.5,45.063V60A3.5,3.5,0,0,0,32,63.5h0A3.5,3.5,0,0,0,35.5,60V45.063L42.969,58a3.5,3.5,0,0,0,4.78,1.281h0a3.5,3.5,0,0,0,1.281-4.78L41.562,41.563,54.5,49.031a3.5,3.5,0,0,0,4.78-1.281h0A3.5,3.5,0,0,0,58,42.97L45.062,35.5H60A3.5,3.5,0,0,0,63.5,32v0A3.5,3.5,0,0,0,60,28.5Z"
    />
  </svg>
);

const previewPlain = (raw) => {
  if (!raw) return '';
  return String(raw)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
};

const PreparedMailsStrip = ({ userId, onOpenPrepared, isLoadingPrepared = false }) => {
  const [items, setItems] = useState([]);
  const [userCollapsed, setUserCollapsed] = useState(() => {
    try {
      return localStorage.getItem('preparedMailsStripCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const [scrolledAway, setScrolledAway] = useState(false);
  const collapsed = userCollapsed || scrolledAway;
  const [dismissing, setDismissing] = useState({});
  const stripRef = useRef(null);
  const headRef = useRef(null);
  const foldInnerRef = useRef(null);
  const cardsRef = useRef(null);
  const [pinTop, setPinTop] = useState(137);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const foldingRef = useRef(false);
  const prevCollapsedRef = useRef(collapsed);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      return undefined;
    }
    const q = query(
      collection(firestore, `World/${userId}/preparedEmails`),
      orderBy('generatedAt', 'desc'),
      limit(MAX_STRIP_ITEMS),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            subject: data.subject || '',
            preview: previewPlain(data.body),
            businessName: data.businessName || '',
            recipientEmail: data.recipientEmail || '',
            // Outreach jobs and flows both write here; show whichever produced it.
            origin: data.jobName || data.flowName || data.topicName || '',
            fromOutreach: data.source === 'outreach_prepare',
            generatedAt: data.generatedAt?.toMillis?.() || 0,
          };
        }));
      },
      () => {
        // A missing composite index or permission hiccup must never break the
        // table around us — the nav's prepared button remains as a fallback.
        setItems([]);
      },
    );
    return () => unsub();
  }, [userId]);

  const toggle = () => {
    setUserCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('preparedMailsStripCollapsed', next ? '1' : '0');
      } catch {
        /* private mode — collapsing just won't persist */
      }
      return next;
    });
  };

  // Fold the chips away once the table is scrolled, so the fixed strip
  // does not eat a third of the viewport. Re-opens at the top unless the
  // user collapsed it themselves.
  useEffect(() => {
    const COLLAPSE_AT = 72;
    const EXPAND_AT = 10;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setScrolledAway((prev) => {
        if (!prev && y > COLLAPSE_AT) return true;
        if (prev && y < EXPAND_AT) return false;
        return prev;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const discard = async (contactId, event) => {
    event.stopPropagation();
    setDismissing((prev) => ({ ...prev, [contactId]: true }));
    try {
      // Deleting the doc is the whole contract: a backend trigger clears the
      // contact's flag and releases the outreach job's parked queue entry.
      await deleteDoc(doc(firestore, `World/${userId}/preparedEmails/${contactId}`));
    } catch (err) {
      console.error('[PreparedMails] Could not discard prepared email:', err);
      setDismissing((prev) => {
        const next = { ...prev };
        delete next[contactId];
        return next;
      });
    }
  };

  const visible = useMemo(
    () => items.filter((item) => !dismissing[item.id]),
    [items, dismissing],
  );

  // Pin the strip under the fixed WorldDb toolbar and reserve the same
  // height in the document flow so the table does not slide underneath.
  useLayoutEffect(() => {
    if (visible.length === 0) {
      setSpacerHeight(0);
      return undefined;
    }
    const foldChanged = prevCollapsedRef.current !== collapsed;
    prevCollapsedRef.current = collapsed;
    if (foldChanged) foldingRef.current = true;
    const nav = document.querySelector('.world-db-header-container');
    const measureSpacer = () => {
      const strip = stripRef.current;
      const head = headRef.current;
      const inner = foldInnerRef.current;
      if (!strip || !head) return 0;
      const pad =
        parseFloat(getComputedStyle(strip).paddingTop) +
        parseFloat(getComputedStyle(strip).paddingBottom);
      const body = collapsed ? 0 : (inner ? inner.scrollHeight : 0);
      return Math.round(head.getBoundingClientRect().height + pad + body);
    };
    const sync = ({ allowSpacer = false } = {}) => {
      // Layout position, not the visual one. Scroll-hide moves the toolbar
      // with translateY, and getBoundingClientRect would follow that and
      // leave the bar parked in the gap once the header comes back.
      const top = nav
        ? Math.round((parseFloat(getComputedStyle(nav).top) || 0) + nav.offsetHeight)
        : 137;
      setPinTop(top);
      if (foldingRef.current && !allowSpacer) return;
      setSpacerHeight(measureSpacer());
    };
    sync({ allowSpacer: foldChanged || !foldingRef.current });
    const ro = new ResizeObserver(() => sync());
    if (nav) ro.observe(nav);
    if (headRef.current) ro.observe(headRef.current);
    if (!collapsed && foldInnerRef.current) ro.observe(foldInnerRef.current);
    window.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [visible.length, collapsed]);

  useEffect(() => {
    if (!foldingRef.current) return undefined;
    const unlock = window.setTimeout(() => {
      foldingRef.current = false;
    }, 220);
    return () => window.clearTimeout(unlock);
  }, [collapsed]);

  const updateCardScroll = () => {
    const el = cardsRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(max > 2 && el.scrollLeft < max - 2);
  };

  useEffect(() => {
    if (collapsed || visible.length === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return undefined;
    }
    const el = cardsRef.current;
    if (!el) return undefined;
    updateCardScroll();
    el.addEventListener('scroll', updateCardScroll, { passive: true });
    const ro = new ResizeObserver(updateCardScroll);
    ro.observe(el);

    // Non-passive: React's onWheel is passive and cannot preventDefault,
    // so the page would keep scrolling instead of the chips going left.
    const onWheel = (event) => {
      const dx = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!dx) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 2) return;
      const next = Math.max(0, Math.min(max, el.scrollLeft + dx));
      if (next === el.scrollLeft) return;
      event.preventDefault();
      el.scrollLeft = next;
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('scroll', updateCardScroll);
      el.removeEventListener('wheel', onWheel);
      ro.disconnect();
    };
  }, [collapsed, visible.length]);

  const scrollCards = (dir) => {
    const el = cardsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' });
  };

  if (visible.length === 0) return null;

  return (
    <>
    <div
      ref={stripRef}
      className={`pm-strip${collapsed ? ' collapsed' : ''}`}
      style={{ top: pinTop }}
    >
      <div className="pm-strip-head" ref={headRef}>
        <button type="button" className="pm-strip-toggle" onClick={toggle}>
          <i className="fa fa-chevron-down"></i>
          <span className="pm-strip-title">Prepared Mails</span>
        </button>
        <button
          type="button"
          className="prepared-mails-badge"
          onClick={() => onOpenPrepared()}
          disabled={isLoadingPrepared}
        >
          <span className="prepared-mails-badge__count">
            {isLoadingPrepared ? '…' : visible.length}
          </span>
          <i className="fa fa-paper-plane" aria-hidden="true"></i>
          <span>Review in Bulk-Mode</span>
        </button>
      </div>

      <div className="pm-strip-fold" aria-hidden={collapsed}>
        <div className="pm-strip-fold-inner" ref={foldInnerRef}>
        <div className={`pm-strip-scroller${canScrollLeft ? ' has-left' : ''}${canScrollRight ? ' has-right' : ''}`}>
          {canScrollLeft && (
            <button type="button" className="pm-strip-arrow left" onClick={() => scrollCards(-1)} aria-label="Scroll left">
              <i className="fa fa-chevron-left"></i>
            </button>
          )}
          <div className="pm-strip-cards" ref={cardsRef}>
          {visible.map((item) => (
            <div
              key={item.id}
              className="pm-card"
              role="button"
              tabIndex={collapsed ? -1 : 0}
              onClick={() => onOpenPrepared([item.id])}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onOpenPrepared([item.id]);
              }}
              title={item.subject || 'Prepared email'}
            >
              <div className="pm-card-top">
                <span className="pm-card-stamp" aria-hidden="true">
                  <i className="fa fa-envelope"></i>
                </span>
                <span className="pm-card-name">
                  {item.businessName || item.recipientEmail || item.id}
                </span>
                <button
                  type="button"
                  className="pm-card-discard"
                  tabIndex={collapsed ? -1 : 0}
                  onClick={(e) => discard(item.id, e)}
                  title="Discard this prepared email"
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
              <div className="pm-card-subject">{item.subject || 'No subject'}</div>
              {item.preview && (
                <div className="pm-card-paper">{item.preview}</div>
              )}
              <div className="pm-card-meta">
                <span className={`pm-card-origin${item.fromOutreach ? ' outreach' : ''}`}>
                  {item.fromOutreach
                    ? <OriginStar />
                    : <i className="fa fa-diagram-project"></i>}
                  {item.origin || (item.fromOutreach ? 'Outreach job' : 'Flow')}
                </span>
                {item.generatedAt > 0 && (
                  <span className="pm-card-time">{relativeTime(item.generatedAt)}</span>
                )}
              </div>
            </div>
          ))}
          </div>
          {canScrollRight && (
            <button type="button" className="pm-strip-arrow right" onClick={() => scrollCards(1)} aria-label="Scroll right">
              <i className="fa fa-chevron-right"></i>
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
    <div className="pm-strip-spacer" style={{ height: spacerHeight }} aria-hidden="true" />
    </>
  );
};

export default PreparedMailsStrip;
